import { Alert, Button, Skeleton, StatusBadge } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { type JSX, useEffect } from 'react';

import { formatCalendarDate } from '../../production/format-calendar-date';
import { getProduction } from '../../production/api';
import { splitByDietaryCriticality } from '../../production/group-lines';
import { useDietaryTagNames, useServiceTypeNames } from '../../production/use-catalog-names';
import { AddendaBlock } from './components/addenda-block';
import { DietaryHighlightBlock } from './components/dietary-highlight-block';
import { ProductionLineTable } from './components/production-line-table';
import './print.css';

const printRoute = getRouteApi('/print-protected/production/$date/print');

/**
 * `production-export/spec.md` → "Página de impresión optimizada para
 * cocina": no `AppShell` (its own route group, `print-protected`, skips
 * the sidebar/header entirely), dietary block first, addenda block last.
 * Never generates a PDF server-side — `window.print()` fires once the
 * sheet has loaded, with a manual "Imprimir" button (hidden on paper via
 * `print:hidden`) for browsers that block auto-print.
 */
export function ProductionSheetPrintPage(): JSX.Element {
  const { date } = printRoute.useParams();
  // The link from the screen names the unit (`?unit=`); the route declares no search schema.
  const search: Record<string, unknown> = printRoute.useSearch();
  const unit = search['unit'];
  const operatingUnitId = typeof unit === 'string' && unit !== '' ? unit : undefined;
  const query = useQuery({
    queryKey: ['production', date, operatingUnitId ?? ''],
    queryFn: () => getProduction(date, operatingUnitId),
  });
  const serviceTypeNames = useServiceTypeNames();
  const dietaryTagNames = useDietaryTagNames();

  useEffect(() => {
    if (query.data !== undefined) {
      window.print();
    }
  }, [query.data]);

  if (query.isPending) {
    return (
      <div className="p-8">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="p-8">
        <Alert tone="danger">No pudimos cargar la planilla para imprimir.</Alert>
      </div>
    );
  }

  const view = query.data;
  const { critical, general } = splitByDietaryCriticality(view.snapshot.lines);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-8 print:p-0">
      <div className="print:hidden">
        <Button icon={Printer} onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>

      <header className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Planilla de Producción</h1>
          {view.snapshot.metadata.operatingUnit === undefined ? null : (
            <p data-testid="production-unit-name" className="text-sm font-medium text-slate-800">
              {view.snapshot.metadata.operatingUnit.name}
            </p>
          )}
          <p className="text-sm text-slate-600">{formatCalendarDate(date)}</p>
        </div>
        <StatusBadge domain="production" status={view.status} />
      </header>

      <DietaryHighlightBlock
        lines={critical}
        expandable={false}
        serviceTypeNames={serviceTypeNames}
        dietaryTagNames={dietaryTagNames}
      />

      {general.length > 0 ? (
        <ProductionLineTable
          lines={general}
          expandable={false}
          serviceTypeNames={serviceTypeNames}
          dietaryTagNames={dietaryTagNames}
        />
      ) : null}

      <AddendaBlock addenda={view.addenda} />
    </div>
  );
}
